<script lang="ts" setup>
	import {compareOrderLineByGroupAndArticleName} from 'dummen-shared';
	import {useI18n} from 'vue-i18n';
	import ShoppingcartOrderLine from '@/components/ShoppingcartOrderLine.vue';
	import type {ShoppingCartOrderLine} from 'dummen-shared/src/interfaces/order.line.type.ts';

	const {t} = useI18n();

	const props = defineProps<{
		orderLines: Record<string, ShoppingCartOrderLine[]>;
		showOrdersWithAvailabilityIssues: boolean;
	}>();

	const getConsignmentLabelForOrderLine = (orderLines: ShoppingCartOrderLine[]) => orderLines[0].consignment ?? null;

	const getSortedOrderLines = (orderLines: ShoppingCartOrderLine[]): ShoppingCartOrderLine[] => {
		const orderLinesCopy = [...orderLines];
		return orderLinesCopy.sort((line1: ShoppingCartOrderLine, line2: ShoppingCartOrderLine) => compareOrderLineByGroupAndArticleName(line1, line2));
	};

	const getUniqueObjectKey = (orderLine: ShoppingCartOrderLine) => {
		return JSON.stringify({
			week: orderLine.week,
			year: orderLine.week,
			form: orderLine.formId,
			consignment: orderLine.consignment,
			location: orderLine.locationId,
			articleId: orderLine.articleId
		});
	};

	const showOrderLine = (orderLine: ShoppingCartOrderLine): boolean => {
		if (!props.showOrdersWithAvailabilityIssues) {
			return true;
		}

		return orderLine.amount > orderLine.amountAvailable && !orderLine.allowOverbooking;
	};

</script>

<template>
	<div class="shoppingcart-table__wrapper">
		<template v-for="(orderLinesRecord, i) in orderLines" :key="`consignment-${i}`">
			<h4 v-if="getConsignmentLabelForOrderLine(orderLinesRecord)" class="consignment-name">
				{{ getConsignmentLabelForOrderLine(orderLinesRecord) }}
			</h4>

			<table class="mydummen-table mydummen-table--order-detail shoppingcart-table"
			       style="width: 100%;">
				<thead>
					<tr>
						<th class="col-remove-icon"></th>
						<th class="col-picture"></th>
						<th class="col-variety">{{ t('shoppingcart.generic.variety.text') }}</th>
						<th class="col-code">{{ t('shoppingcart.generic.code.text') }}</th>
						<th class="col-group">{{ t('shoppingcart.generic.group.text') }}</th>
						<th class="col-form">{{ t('shoppingcart.generic.form.text') }}</th>
						<th class="col-consignment">{{ t('shoppingcart.generic.consignment.text') }}</th>
						<th class="col-action"></th>
						<th class="col-action"></th>
						<th class="col-amount" colspan="2">{{ t('shoppingcart.generic.amountAndAvailability.text') }}</th>
						<th class="col-warning"></th>
					</tr>
				</thead>

				<tbody>
					<template v-for="line in getSortedOrderLines(orderLinesRecord)"
					          :key="'orderline-' + getUniqueObjectKey(line)">
						<shoppingcart-order-line
								v-if="showOrderLine(line)"
								:orderLine="line"
						/>
					</template>
				</tbody>
			</table>
		</template>
	</div>
</template>

<style scoped>
	.shoppingcart-table {
		width: 100%;
		table-layout: fixed;
	}

	.col-remove-icon { width: 3%; }
	.col-picture { width: 4%; }
	.col-variety { width: 18%; }
	.col-code { width: 6%; }
	.col-group { width: 15%; }
	.col-form { width: 7%; }
	.col-consignment { width: 18%; }
	.col-action { width: 4%; }
	.col-amount {
		width: 14%;
		white-space: normal;
		word-break: break-word;}
	.col-warning { width: 2%; }
</style>
